import { useTranslation } from 'react-i18next';
import { AlertCircle, History } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatDateTime } from '~/lib/format-date';
import { useLoyaltyCustomerHistory } from '~/hooks/use-loyalty-customers';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	customerId: number | null;
	customerName?: string;
}

/**
 * Per-customer loyalty points history (grouped events). Maps the event-type and point-status enums
 * to localized labels and prefixes the order UID with the event description (legacy parity:
 * Core/client/admin/src/pages/System/PointCustomers/HistoryPoints.js).
 */
export function LoyaltyHistoryDialog({ open, onOpenChange, customerId, customerName }: Props) {
	const { t } = useTranslation();
	const { data, isLoading, isError } = useLoyaltyCustomerHistory(open ? customerId : null);
	const rows = data ?? [];

	const eventLabel = (etid?: number) =>
		etid == null ? '—' : t(`loyalty.customers.eventTypes.${etid}`, { defaultValue: t('common.unknown') });
	const statusLabel = (ptst?: number) =>
		ptst == null ? '—' : t(`loyalty.customers.pointStatus.${ptst}`, { defaultValue: t('common.unknown') });

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{/* sm: as the other wide dialogs: the base dialog's sm:max-w-lg overrode max-w-3xl and cut the status column off. */}
			<DialogContent className="max-w-[95vw] sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t('loyalty.customers.history.title')}</DialogTitle>
					<DialogDescription>{customerName || t('loyalty.customers.history.subtitle')}</DialogDescription>
				</DialogHeader>

				{isError ? (
					<div className="flex flex-col items-center gap-3 py-12 text-center">
						<AlertCircle size={36} className="text-danger-fg" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('loyalty.loadError')}</p>
					</div>
				) : isLoading ? (
					<div className="flex flex-col gap-2 py-2">
						{Array.from({ length: 5 }).map((_, i) => (
							<Skeleton key={i} className="h-9 w-full rounded-md" />
						))}
					</div>
				) : rows.length === 0 ? (
					<div className="flex flex-col items-center gap-3 py-12 text-center">
						<History size={32} className="text-foreground-disabled" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
					</div>
				) : (
					<div className="max-h-[60vh] overflow-auto rounded-md border border-border">
						<table className="w-full border-collapse text-sm">
							<thead className="sticky top-0 bg-elevated text-xs text-foreground-muted">
								<tr className="border-b border-border">
									<th className="px-3 py-2 text-left font-medium">{t('loyalty.customers.history.columns.description')}</th>
									<th className="px-3 py-2 text-left font-medium">{t('loyalty.customers.history.columns.date')}</th>
									<th className="px-3 py-2 text-right font-medium">{t('loyalty.customers.history.columns.points')}</th>
									<th className="px-3 py-2 text-left font-medium">{t('loyalty.customers.history.columns.status')}</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((h, i) => (
									<tr key={`${h.uid ?? ''}-${i}`} className="border-b border-border/60">
										<td className="px-3 py-2 text-foreground">
											{eventLabel(h.etid)}
											{h.uid ? <span className="ml-1 font-mono text-xs text-foreground-muted">#{h.uid}</span> : null}
											{h.nt ? (
												<span className="block text-xs text-foreground-muted">
													{h.aby ? t('loyalty.customers.history.noteBy', { note: h.nt, admin: h.aby }) : h.nt}
												</span>
											) : null}
										</td>
										<td className="px-3 py-2 text-foreground-muted">{formatDateTime(h.crdt)}</td>
										<td className="px-3 py-2 text-right tabular-nums text-foreground">{h.pnt ?? 0}</td>
										<td className="px-3 py-2">
											<Badge variant={h.ptst === 1 ? 'default' : h.ptst === 3 ? 'outline' : 'secondary'}>{statusLabel(h.ptst)}</Badge>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
